/**
 * Tunable game rules shared by the server and the client.
 * The server is authoritative; the client uses these for prediction and display.
 */

/** Simulation + network tick rate (Hz). */
export const TICK_RATE = 20;
export const TICK_MS = 1000 / TICK_RATE;
export const DT = 1 / TICK_RATE;

/** The world is a square of this many units (1 unit ~= 1 pixel at zoom 1). */
export const WORLD_SIZE = 50000;

/** Dead centre of the map, where the hub city stands. */
export const CITY_CENTER = { x: WORLD_SIZE / 2, y: WORLD_SIZE / 2 };

/** The walled hub: monsters cannot enter it, and players respawn inside it. */
export const CITY_RADIUS = 1400;
export const CITY_WALL_RADIUS = 1394;

/** Where players appear when they join or die - south of the fountain. */
export const SPAWN_POINT = { x: CITY_CENTER.x, y: CITY_CENTER.y + 430 };

/** Vendor stalls, relative to the city centre. Marrow handles potions and scrolls. */
export const TRADER_OFFSET = { x: -560, y: 330 };
export const TRADER_NAME = 'Marrow the Fence';
export const WEAPONER_NAME = 'Brynja the Bladesmith';
export const WEAPONER_OFFSET = { x: -560, y: -330 };
export const ARMOURER_NAME = 'Havard the Armorer';
export const ARMOURER_OFFSET = { x: 560, y: -330 };
/** How close you must stand to buy from any vendor. */
export const TRADE_RANGE = 330;

/** The gate guards standing either side of the south gate. */
export const GUARD_NAMES = ['Sanđin', 'Priško'];

/** The bounty officer who hands out kill quests. */
export const QUEST_GIVER_NAME = 'Warden Ilse';
export const QUEST_GIVER_OFFSET = { x: 520, y: 250 };
/** How close you must stand to accept or read bounties. */
export const QUEST_RANGE = 330;

/** The leaderboard monument, standing north of the fountain. */
export const MONUMENT_NAME = 'Monument of Legends';
export const MONUMENT_OFFSET = { x: 0, y: -360 };
export const MONUMENT_RANGE = 300;
/** How many names the monument remembers. */
export const LEADERBOARD_SIZE = 10;

/**
 * The ring around the city that monsters are kept out of entirely. The apron
 * between the wall and this line is where you meet friends before heading out.
 */
export const SAFE_ZONE_RADIUS = 1720;

/** Wilderness scenery. Sparse enough that the world reads as open and wild. */
export const ROCK_COUNT = 11000;
export const TREE_COUNT = 7000;
/** Open ground between the city wall and the first wild obstacle. */
export const CITY_CLEARING = 900;

/**
 * Points of interest are placed by dicing the world into square chunks and
 * giving each one a small, seeded chance to host a landmark. A hash of the
 * world seed and the chunk coordinates is the only input, so the client and
 * server independently stamp byte-identical POIs - the same trick the rest of
 * the scenery uses. With a 20 x 20 grid and the chance below that lands around
 * fifteen landmarks, so each one stays a genuine find rather than wallpaper.
 */
export const POI_CHUNK_SIZE = 2500;
export const POI_CHANCE_PER_CHUNK = 0.028;
/** Landmarks are kept this far beyond the sanctuary, on top of their footprint. */
export const POI_CITY_MARGIN = 700;
/** Open ground reserved around a landmark so trees do not grow through it. */
export const POI_CLEARING = 90;
/**
 * A landmark puts its own stamp on the wildlife around it. A monster spawned
 * within this of a POI draws from that landmark's themed spawn table, so a
 * graveyard crawls with the restless and a camp swarms with raiders. Set wider
 * than the monster spawn ring so standing at a landmark genuinely feels like
 * entering its den.
 */
export const POI_DEN_RADIUS = 1900;

/**
 * Shrines and chapels are places of rest. Linger within their grounds and you
 * recover - but taking a hit breaks the calm for a moment, so this is a reward
 * for exploring and disengaging, not free healing mid-fight.
 */
export const POI_REST_RADIUS = 220;
export const POI_REST_HP_PER_SEC = 0.08;
export const POI_REST_MP_PER_SEC = 0.1;
export const POI_REST_CALM_MS = 3000;

/** Drops. */
export const PICKUP_RADIUS = 46;
/** Ground loot vanishes after two minutes. */
export const DROP_LIFETIME_MS = 120000;
/**
 * Loot is briefly un-pickable so it is actually visible on the ground before
 * being hoovered up - otherwise a kill at your feet looks like it drops nothing.
 */
export const DROP_ARM_DELAY_MS = 550;
/** Loot blinks for this long before it disappears, as a warning. */
export const DROP_FADE_MS = 12000;
/**
 * Coin income. Deliberately modest, and growing slower than experience, so a
 * high-level character has to actually work for money rather than swimming in
 * it. The smiths' upgrade service is the main thing worth saving for.
 */
export const COINS_PER_XP = 0.2;
export const COIN_LEVEL_EXP = 0.9;
export const COIN_VARIANCE = 0.4;
export const HEALTH_DROP_CHANCE = 0.13;
export const MANA_DROP_CHANCE = 0.13;
export const SCROLL_DROP_CHANCE = 0.03;
/** Chance a kill also leaves a piece of gear behind. */
export const GEAR_DROP_CHANCE = 0.08;

/**
 * Elites: rare, tougher versions of ordinary monsters. They are the cheapest
 * source of encounter variety and a reliable gear drop, so a fight worth
 * stopping for shows up every so often.
 */
export const ELITE_CHANCE = 0.08;
export const ELITE_HP_MULT = 2.6;
export const ELITE_ATTACK_MULT = 1.5;
export const ELITE_DEFENSE_MULT = 1.6;
export const ELITE_XP_MULT = 3;

/** How many loose gear pieces a character can carry before drops stop being collected. */
export const MAX_INVENTORY = 30;

/** Bag and wallet limits. */
export const MAX_BAG = 99;
export const MAX_COINS = 9_999_999;
/** Most items a single purchase can buy. */
export const MAX_PURCHASE = 10;

/** How many gear pieces the trader has in stock at once, and how often it turns over. */
export const SHOP_GEAR_COUNT = 4;
export const SHOP_GEAR_REFRESH_MS = 180000;

/**
 * Hit boxes are slightly larger than the artwork so that grazing shots and
 * near-misses still connect. Applied to monsters only - the player keeps a
 * tight box, which keeps incoming damage fair.
 */
export const ENEMY_HIT_SCALE = 1.32;

/**
 * Monster level ramp. Everything inside the apron around the city is level 1,
 * and every step further out adds a level, so the far corners of an enormous
 * map are genuinely lethal. With the current numbers: level 1 within ~2.3k of
 * the city, ~level 20 out past the landmarks, and ~level 95 in the corners.
 */
export const LEVEL_RAMP_START = CITY_RADIUS + 900;
/**
 * The ramp has to stretch far enough to cover 100 player levels: at this step
 * the far corners of the map sit around level 95. Steepening it (from 600) also
 * means the low-level country around the city is smaller than it used to be.
 */
export const LEVEL_RAMP_STEP = 350;
export const MAX_ENEMY_LEVEL = 98;

/**
 * Experience is gated by the level gap, so farming trivial monsters stops
 * paying and pushing into dangerous country is rewarded.
 */
export const GREY_LEVEL_GAP = 8;
export const XP_ABOVE_LEVEL_BONUS = 0.04;
export const XP_ABOVE_LEVEL_CAP = 0.6;
/**
 * Safety net: monsters never spawn more than this far above their target, so a
 * new player who wanders off the edge of the world is challenged rather than
 * deleted on sight. Kept generous so distance still clearly rules.
 */
export const SPAWN_LEVEL_HEADROOM = 10;

/** Obstacles are generated from this seed so client and server agree. */
export const WORLD_SEED = 1337;

/** Only entities within this radius of a player are networked to them. */
export const INTEREST_RADIUS = 2600;

export const PLAYER_RADIUS = 15;

/**
 * Monster population is per player, not a flat world-wide number. Each player
 * keeps roughly this many monsters alive around them; spawning always feeds
 * whoever currently has the fewest, so a group that spreads out still gets an
 * even share instead of one person being buried while everyone else wanders an
 * empty map.
 */
export const ENEMIES_PER_PLAYER = 56;
/** Used to decide which player is currently the hungriest for monsters. */
export const ENEMY_POPULATION_RADIUS = 2200;
/** Ceiling for a full group, so five players do not ask for 350 monsters. */
export const MAX_ENEMY_COUNT = 280;

/**
 * A monster further than this from every player is a stray: nobody can see it,
 * nobody is fighting it. It is recycled after the grace period so the
 * population budget is always spent where people actually are.
 *
 * The sanctuary still pushes monsters clear of the city walls, so the inside of
 * the city stays empty even though the fields beyond the gate are busy.
 */
export const ENEMY_DESPAWN_DISTANCE = 3000;
export const ENEMY_DESPAWN_GRACE = 5;

/** Monsters appear just outside a comfortable view of their target player. */
export const ENEMY_SPAWN_MIN_DISTANCE = 1200;
export const ENEMY_SPAWN_SPREAD = 700;

/**
 * World bosses: one lurks at each lair landmark. It wakes when a player comes
 * close, and once slain stays gone for a good while so it is an event rather
 * than a farm.
 */
export const BOSS_ACTIVATE_RANGE = 2200;
export const BOSS_RESPAWN_MS = 12 * 60 * 1000;
/** Bosses are this many levels above the monsters around their lair. */
export const BOSS_LEVEL_BONUS = 4;
/** Only these landmark kinds harbour a boss. */
export const BOSS_LAIRS: Record<string, string> = {
  graveyard: 'The Grave-Wight',
  ruins: 'Ruin Colossus',
  watchtower: 'The Tower Warden',
  camp: 'Bandit Chieftain',
  chapel: 'The Fallen Priest',
};

/** Townsfolk pipe up when a player wanders close. */
export const NPC_CHATTER_RANGE = 350;
/** How long a single NPC stays quiet after speaking. */
export const NPC_CHATTER_COOLDOWN_MS = 24000;
export const NPC_CHATTER_VARIANCE_MS = 26000;
/** How long a speech bubble lingers - shared so the server can avoid overlaps. */
export const NPC_CHATTER_BUBBLE_MS = 6000;
/**
 * Two NPCs closer together than this will not both have a bubble up at once,
 * which is what stops the two gate guards talking over each other. NPCs further
 * apart than this never overlap on screen, so they are free to both speak.
 */
export const NPC_CHATTER_SEPARATION = 520;

export const MAX_LEVEL = 100;
export const STAT_POINTS_PER_LEVEL = 3;

/** Fraction of the progress toward the next level lost on death (old-school). */
export const XP_LOSS_ON_DEATH = 0.1;

export const RESPAWN_MS = 5000;
export const INVULN_AFTER_RESPAWN_MS = 2000;

/** Combat feel. */
export const CRIT_CHANCE = 0.12;
export const CRIT_MULTIPLIER = 1.75;
export const DAMAGE_VARIANCE = 0.15;
/** Melee attacks are a cone: half-angle in radians. */
export const MELEE_ARC = Math.PI / 2.6;
/** A melee hit still lands if the target is basically touching you. */
export const MELEE_GRACE_RANGE = 22;

/** Per-point attribute bonuses. */
export const STR_ATTACK = 2.5;
export const AGI_SPEED = 4;
export const AGI_HASTE = 0.006; // cooldown reduction per point
export const MAX_HASTE = 0.45;
export const VIT_HEALTH = 8;

export const CHAT_MAX_LENGTH = 140;
/** Minimum gap between messages from one player. */
export const CHAT_COOLDOWN_MS = 1200;
/** Saying the exact same thing again inside this window is refused. */
export const CHAT_REPEAT_WINDOW_MS = 5000;
/** How many blocked attempts before the player is actually told to slow down. */
export const CHAT_WARN_AFTER = 3;

/** Social. */
export const MAX_PARTY_SIZE = 5;
/** Party members inside this range share experience and bounty credit. */
export const PARTY_SHARE_RANGE = 2200;
/** Each extra member adds this much to the pool before it is split. */
export const PARTY_XP_BONUS = 0.1;
export const MAX_FRIENDS = 60;
/** How long an unanswered party invite sits around, in ms. */
export const PARTY_INVITE_TTL_MS = 60000;
export const CHAT_BUBBLE_MS = 6000;
export const NAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{2,13}$/;
